import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useActiveAccount } from "@/hooks/useAccounts";
import { useConnectionStatus } from "@/hooks/useConnectionStatus";
import { resolveAccountKind } from "@/lib/accountKind";
import type { StatusBarMode } from "../types";

interface ResolvedStatus {
  mode: StatusBarMode;
  reason?: string;
}

/**
 * Resolves which StatusBar mode to render given the active account.
 * Priority:
 *   1) Active account is sample (is_mock)        → "sample"
 *   2) Active account is real & connected        → "real"
 *   3) Active account is demo (broker) connected → "demo"
 *   4) Has account but disconnected              → "disconnected"
 *   5) No account & not a trader                 → "hidden"
 *   6) Trader with no account at all             → "sample" (will show empty/sample CTA)
 */
export const useStatusBarMode = (): ResolvedStatus => {
  const { user } = useAuth();
  const { role } = useUserRole();
  const { data: account } = useActiveAccount();
  const { status } = useConnectionStatus(account?.id);

  return useMemo<ResolvedStatus>(() => {
    if (!user || role !== "trader") return { mode: "hidden" };
    if (!account) return { mode: "sample" };

    const kind = resolveAccountKind(account);

    if (kind === "sample") return { mode: "sample" };
    if (status !== "connected") return { mode: "disconnected" };
    if (kind === "real") return { mode: "real" };
    return { mode: "demo" };
  }, [user, role, account, status]);
};
